import { useEffect, useState } from 'react';
import { usePersistentCallback } from './hooks';
/**
 * Hook to detect if content is clamped by CSS (overflow hidden with line-clamp or max-height)
 *
 * This hook uses ResizeObserver to efficiently detect when content size changes
 * and determines if the content is being clipped by comparing scrollHeight with clientHeight.
 *
 * Common use cases:
 * - Detecting if text is truncated with line-clamp
 * - Showing "Show more" / "Show less" buttons when content overflows
 * - Detecting if content needs scrolling
 *
 * @example
 * ```tsx
 * const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
 * const contentElement = containerRef?.querySelector('.content-class') ?? null;
 *
 * const isClamped = useIsContentClamped({
 *   element: contentElement,
 *   enabled: !expanded
 * });
 *
 * // In component:
 * <div ref={setContainerRef}>
 *   <div className="content-class">
 *     // content that might be clamped
 *   </div>
 * </div>
 * {isClamped && <button>Show more</button>}
 * ```
 */
export function useIsContentClamped({ element, enabled = true }) {
    const [isClamped, setIsClamped] = useState(false);
    const checkClamped = usePersistentCallback(() => {
        if (!element || !enabled) {
            setIsClamped(false);
            return;
        }
        // Check if content is clamped by comparing scroll height with client height
        setIsClamped(element.scrollHeight > element.clientHeight);
    });
    useEffect(() => {
        if (!enabled || !element) {
            setIsClamped(false);
            return;
        }
        // Initial check
        checkClamped();
        // Use ResizeObserver to detect when content size changes
        const resizeObserver = new ResizeObserver(checkClamped);
        resizeObserver.observe(element);
        return () => {
            resizeObserver.disconnect();
        };
    }, [element, enabled, checkClamped]);
    return isClamped;
}
